import React, { useState, useEffect, useRef } from 'react';
import 'aframe';
import { fetchSceneElementsDirect as fetchSceneElements } from '../utils/api';
import '../styles/GalleryViewer.css';

function GalleryViewer({ viewMode = '3d' }) {
  const [sceneData, setSceneData] = useState({
    walls: [],
    artworks: [],
    boxes: [],
    easels: [],
    furniture: []  // Add furniture array
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const sceneRef = useRef(null);

  // Fetch and render scene data
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.group('Fetch Scene Data DEBUG');
        
        const collectionId = new URLSearchParams(window.location.search).get('collection_id');
        console.log("URL collection_id parameter:", collectionId);

        if (!collectionId) {
          console.log("No collection_id in URL, redirecting to collection 200");
          window.location.href = window.location.pathname + '?collection_id=200';
          return;
        } else {
          console.log("Using collection_id from URL:", collectionId);
        }
        
        console.log("Extracted Collection ID:", collectionId);
        console.log("View Mode:", viewMode);
  
        if (!collectionId) {
          console.warn("No collection ID provided");
          setError("No collection ID provided");
          setIsLoading(false);
          console.groupEnd();
          return;
        }
  
        // Fetch scene elements
        const rawData = await fetchSceneElements(collectionId, viewMode);
        
        console.log("Raw Fetched Data:", rawData);
        console.log("Data Type:", typeof rawData);
        console.log("Data Keys:", rawData ? Object.keys(rawData) : 'No data');
  
        // Determine how to process the data based on its structure
        let processedData;
        if (Array.isArray(rawData)) {
          console.log("Processing array data");
          processedData = {
            walls: rawData.filter(el => el.type !== 'wall_2d'),
            artworks: rawData.filter(el => el.type === 'artwork'),
            boxes: rawData.filter(el => el.type === 'box'),
            easels: rawData.filter(el => el.type === 'easel'),
            furniture: rawData.filter(el => el.type === 'furniture')  // Add furniture filtering
          };
        } else if (typeof rawData === 'object' && rawData !== null) {
          console.log("Using object data directly");
          processedData = rawData;
        } else {
          console.warn("Unexpected data format");
        }
  
        console.log("Processed Data:", processedData);
  
        const hasElements = Object.values(processedData).some(arr => arr.length > 0);
        
        if (!hasElements) {
          console.warn("No elements found in the collection");
          setError("No elements found in the collection");
        }
  
        setSceneData(processedData);
        setIsLoading(false);
  
        if (processedData.walls.length > 0 && processedData.walls[0].collection_name) {
          document.getElementById('collectionTitle').textContent = processedData.walls[0].collection_name;
        }
  
        console.groupEnd();
      } catch (error) {
        console.error('Error fetching scene elements:', error);
        setError(error.message || 'Failed to fetch scene elements');
        setIsLoading(false);
        console.groupEnd();
      }
    };
  
    fetchData();
  }, [viewMode]);

  // Render loading state
  if (isLoading) {
    return (
      <div id="loading-overlay">
        <div className="spinner-border loading-spinner" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h4>Loading Gallery...</h4>
        <div id="loading-status" className="mt-2"></div>
      </div>
    );
  }

  // Render A-Frame scene
  return (
    <>
      {/* Loading overlay */}
      {isLoading && (
        <div id="loading-overlay">
          <div className="spinner-border loading-spinner" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4>Loading Gallery...</h4>
        </div>
      )}

      {/* Collection Info */}
      <div className="collection-info" id="collectionInfo">
        <h5 id="collectionTitle">Gallery Viewer</h5>
        <p className="mb-0 small">Pan to navigate • Pinch to zoom</p>
      </div>

      {/* A-Frame Scene */}
      <a-scene ref={sceneRef}>
        <a-assets>
          <img id="groundTexture" src="/images/retro.jpg" />
          <img id="skyTexture" src="/images/gradient-sky-svg.svg" />
        </a-assets>

        <a-entity 
          id="camera" 
          camera 
          position="27 4.42 9" 
          rotation="-2 30 0" 
          wasd-controls="acceleration: 50" 
          look-controls
        >
          <a-cursor 
            id="cursor" 
            raycaster="objects: .clickable;" 
            fuse="false" 
            material="color: white;"
          ></a-cursor>
        </a-entity>

        <a-plane 
          src="#groundTexture" 
          rotation="-90 0 0" 
          width="3000" 
          height="3000" 
          material="repeat: 3000 3000;"
        ></a-plane>
        
        <a-sky src="#skyTexture" rotation="0 -90 0"></a-sky>

        {/* Walls Container */}
        <a-entity id="walls-container">
          {sceneData.walls.filter(wall => wall.type !== 'wall_2d').map((wall, index) => {
            const width = (wall.width || 120) / 12;
            const height = (wall.height || 96) / 12;
            const depth = 0.5 / 12;

            const materialProps = wall.source 
              ? `src: ${wall.source}; repeat: 4 4` 
              : `color: ${wall.color || '#F5F5F5'}`;

            return (
              <a-entity 
                key={wall.element_id || index}
                id={`wall-${wall.vc_id || wall.element_id}`}
                position={`${(wall.position_x || 0)/12} ${(wall.position_y || 0)/12} ${(wall.position_z || 0)/12}`}
                rotation={`${wall.rotation_x || 0} ${wall.rotation_y || 0} ${wall.rotation_z || 0}`}
                geometry={`primitive: box; width: ${width}; height: ${height}; depth: ${depth}`}
                material={`shader: standard; ${materialProps}`}
              ></a-entity>
            );
          })}
        </a-entity>

        {/* Scene Container for Furniture */}
        <a-entity id="furniture-container">
          {sceneData.furniture.map((el, index) => (
              <a-entity 
              key={el.element_id || index}
              id={`element-${el.element_id || index}`}
              position={`${el.position_x/12} ${el.position_y/12} ${el.position_z/12}`}
              rotation={`${el.rotation_x || 0} ${el.rotation_y || 0} ${el.rotation_z || 0}`}
              scale={`${el.scale_x || 1} ${el.scale_y || 1} ${el.scale_z || 1}`}
              gltf-model={el.source}
              material="shader: standard; transparent: true; opacity: 1.0"
            ></a-entity>
          ))}
        </a-entity>

        {/* Scene Container for Artworks */}
        <a-entity id="scene-container">
          {[...sceneData.artworks, ...sceneData.boxes, ...sceneData.easels].map((el, index) => {
            const width = (el.width || 24) / 12;
            const height = (el.height || 24) / 12;
            
            return (
              <a-entity 
                key={el.vc_id || index}
                id={`element-${el.vc_id || index}`}
                position={`${el.position_x/12} ${el.position_y/12} ${el.position_z/12}`}
                rotation={`${el.rotation_x || 0} ${el.rotation_y || 0} ${el.rotation_z || 0}`}
              >
                {el.type === 'box' ? (
                  <a-box 
                    width={width} 
                    height={height} 
                    depth={width} 
                    color={el.color || '#808080'}
                  />
                ) : (
                  <>
                    <a-box 
                      width={width} 
                      height={height} 
                      depth="0.1" 
                      color="#444444"
                    />
                    <a-plane 
                      position="0 0 0.09" 
                      width={width} 
                      height={height} 
                      src={el.source}
                    />
                  </>
                )}
              </a-entity>
            );
          })}
        </a-entity>
      </a-scene>

      {/* Camera Controls */}
      <div className="camera-controls">
        <div className="d-flex flex-wrap justify-content-center">
          <button className="btn btn-sm btn-outline-secondary" onClick={() => moveCamera('-3 4.42 0.48', '-14 -171 0')}>Front Wall</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => moveCamera('-6 4.42 9', '-1 -15 0')}>Left Wall</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => moveCamera('27 4.42 9', '-2 -16 0')}>Right Wall</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => moveCamera('90 4.42 9', '-4 -18 0')}>Seated</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => moveCamera('17 12 -25', '30 0 0')}>Overview</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => moveCamera('0 30 20', '90 -29 -53')}>Bird's Eye</button>
        </div>
      </div>
    </>
  );
}

// Helper function to move camera
function moveCamera(position, rotation) {
  const camera = document.getElementById('camera');
  if (camera) {
    camera.setAttribute('position', position);
    camera.setAttribute('rotation', rotation);
  }
}

export default GalleryViewer;